<template>
  <div class="inventoryLabResultCreate">
    <v-breadcrumbs>
      <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }"
        >Home</v-breadcrumbs-item
      >
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item
        :exact="true"
        :to="{ name: 'inventoryLabResult', params: {} }"
      >
        Inventory Lab Result
      </v-breadcrumbs-item>
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item
        :to="{ name: 'inventoryLabResultUpload', params: {} }"
      >
        Upload
      </v-breadcrumbs-item>
    </v-breadcrumbs>

    <v-card class="py-10">
      <v-card-title>
        Lab Result Upload
        <v-spacer />
        <v-btn icon small @click="dialog = true">
          <v-icon>mdi-information-outline</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-file-input
          accept="text/csv,application/pdf"
          multiple
          show-size
          counter
          :rules="[
            (value) =>
              !value ||
              value.every((f) =>
                ['csv', 'pdf'].includes(
                  (f.name || '').split('.').pop().toLowerCase()
                )
              ) ||
              'Only CSV or PDF files allowed',
          ]"
          label="Choose lab result file(s) — CSV or PDF"
          hint="You can select several PDF reports at once."
          persistent-hint
          v-model="selectedFiles"
          :disabled="parsing"
        />
      </v-card-text>
      <v-card-actions>
        <v-btn @click="goBack"> Back </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="parsing || loading || !selectedFiles.length"
          :loading="parsing"
          @click="uploadLabResults"
        >
          {{ parsing ? "Parsing…" : "Upload" }}
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>

    <v-dialog v-model="dialog" max-width="560px">
      <v-card>
        <v-card-title>Supported File Formats</v-card-title>
        <v-card-text>
          <h4>CSV (multiple results)</h4>
          <ul>
            <li>
              Columns named either "Date" or "Analysis Date" for the date.
            </li>
            <li>A column named "Time" for the time.</li>
            <li>
              A column named either "AverOutN" or "Charge" for the document
              number.
            </li>
            <li>
              Additional columns named "Pb", "Sn", "Sb", "Ca", "Al", "Bi", "Cu",
              "Ni", "As", "Ag", "Fe", "Zn", "Te", "Se", "S", and "Cd" for lab
              elements.
            </li>
            <li>
              Optionally: "SampleNo" or "Sample No.", "Quality", "Program",
              "QMDelete" or "Operator".
            </li>
            <li>
              Date format "DD/MM/YYYY", time format "HH:mm:ss".
            </li>
          </ul>
          <h4 class="mt-4">PDF (one result per file — select several at once)</h4>
          <ul>
            <li>
              Direct export from the spectrometer — Spark Q4, Bruker Q8, or
              Thermo XL3 (XRF).
            </li>
            <li>
              The PDF must have selectable text. Scanned / image-only PDFs are
              not supported; please re-export the report as a digital PDF.
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="previewDialog" max-width="900px">
      <v-card>
        <v-card-title>Preview ({{ previewData.length }} rows)</v-card-title>
        <v-card-text>
          <v-alert v-if="futureDateFound" type="warning" dense>
            {{ warningMessage }}
          </v-alert>
          <v-alert v-if="failedFiles.length" type="warning" dense>
            {{ failedFiles.length }} file(s) could not be parsed and were
            skipped:
            <ul>
              <li v-for="f in failedFiles" :key="f.name">
                {{ f.name }} — {{ f.reason }}
              </li>
            </ul>
          </v-alert>
          <div class="table-scroll">
            <v-simple-table>
              <template>
                <thead>
                  <tr>
                    <th v-for="header in previewHeaders" :key="header">
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in previewData" :key="i">
                    <td
                      class="nowrap"
                      v-for="header in previewHeaders"
                      :key="header"
                    >
                      <span v-if="header == 'testTime'">
                        {{ row[header] | formatDateTimeYear }}
                      </span>
                      <span v-else>
                        {{ row[header] }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-card-text>
        <v-card-actions class="justify-space-around">
          <v-btn color="primary" :disabled="futureDateFound" @click="confirmUpload" :loading="loading">
            Confirm and Upload
          </v-btn>
          <v-btn @click="previewDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import csv from "csvtojson";
import { mapActions } from "vuex";
// import axiosObj from "axios";
import moment from "moment";

export default {
  name: "inventoryLabResult",
  data: () => {
    return {
      selectedFiles: [],
      allElements: null,
      loading: false,
      parsing: false,
      dialog: false,
      preparedRows: [],
      previewDialog: false,
      previewData: [],
      previewHeaders: [],
      failedFiles: [],
      warningMessage: "",
      futureDateFound: false,
    };
  },
  created() {
    this.$emit("showParent", false);
    this.initLabElements();
  },
  methods: {
    ...mapActions("inventoryLabResult", [
      "createInventoryLabResult",
      "createAllInventoryLabResults",
      "parseLabResultPdf",
    ]),
    ...mapActions("labElement", ["fetchLabElements"]),
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),
    initLabElements() {
      const params = {
        page: 0,
        size: 200,  // lab_element: 16 rows on production
      };
      this.fetchLabElements(params).then((response) => {
        this.allElements = response.content;
      });
    },
    // Parse every selected file (CSV or PDF), combine into one preview.
    async uploadLabResults() {
      if (!this.selectedFiles || !this.selectedFiles.length) {
        this.addErrorMessage("Please choose at least one file");
        return;
      }
      this.parsing = true;
      this.failedFiles = [];

      const allRows = [];
      const failed = [];

      const worker = async (file) => {
        const ext = (file.name || "").split(".").pop().toLowerCase();
        try {
          if (ext === "csv") {
            const rows = await this.parseCsvFile(file);
            if (!rows.length) {
              failed.push({ name: file.name, reason: "No usable rows found" });
            } else {
              allRows.push(...rows);
            }
          } else if (ext === "pdf") {
            allRows.push(await this.parsePdfFile(file));
          } else {
            failed.push({ name: file.name, reason: "Unsupported file type" });
          }
        } catch (err) {
          failed.push({ name: file.name, reason: this.describeError(err) });
        }
      };

      await this.runWithConcurrency(this.selectedFiles, 5, worker);

      this.parsing = false;
      this.failedFiles = failed;

      if (!allRows.length) {
        this.addErrorMessage(
          "No usable lab results found in the selected file(s)"
        );
        return;
      }
      this.showPreview(allRows);
    },
    // Run `worker` over items with a bounded number of concurrent calls.
    runWithConcurrency(items, limit, worker) {
      const queue = items.slice();
      const next = async () => {
        while (queue.length) {
          await worker(queue.shift());
        }
      };
      const runners = [];
      for (let i = 0; i < Math.min(limit, items.length); i++) {
        runners.push(next());
      }
      return Promise.all(runners);
    },
    parseCsvFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            csv()
              .fromString(reader.result)
              .then((csvRows) => {
                const filteredRows = csvRows.filter((row) => {
                  const date = row.Date ? row.Date : row["Analysis Date"];
                  const time = row.Time;
                  const documentNo = row.AverOutN ? row.AverOutN : row.Charge;
                  return date && time && documentNo;
                });
                resolve(filteredRows.map(this.mapRows));
              })
              .catch(reject);
          } catch (e) {
            reject(e);
          }
        };
        reader.onerror = () => reject(new Error("Could not read file"));
        reader.readAsBinaryString(file);
      });
    },
    async parsePdfFile(file) {
      const dto = await this.parseLabResultPdf(file);
      return this.mapPdfDtoToRow(dto);
    },
    describeError(err) {
      // The axios interceptor exposes the body on err.data; fall back to err.response.
      const body =
        (err && err.data) || (err && err.response && err.response.data) || null;
      if (body && body.message) return body.message;
      if (body && body.code) return body.code;
      if (err && err.message) return err.message;
      return "Could not parse this file";
    },
    showPreview(preparedRows) {
      if (!preparedRows || preparedRows.length === 0) {
        this.addErrorMessage("No usable lab results found");
        return;
      }
      const { previewData, previewHeaders } =
        this.generatePreviewDataAndHeaders(preparedRows);
      this.preparedRows = preparedRows;
      this.previewData = previewData;
      this.previewHeaders = previewHeaders;
      this.previewDialog = true;
    },
    mapPdfDtoToRow(dto) {
      // Convert the backend InventoryLabResultDTO into the same shape mapRows
      // produces for CSV uploads. The DTO already has labElementId per element;
      // we just add the display symbol back so the preview can label columns
      // and so uploadRows' element-stripping destructure stays consistent.
      const elements = (dto.elements || []).map((e) => ({
        labElementId: e.labElementId,
        element: this.getElementSymbol(e.labElementId),
        compositionPercent: e.compositionPercent,
        lessThan: !!e.lessThan,
      }));
      return {
        sampleName: dto.sampleName,
        quality: dto.quality,
        averoutN: dto.averoutN,
        program: dto.program,
        technician: dto.technician,
        elements,
        testTime: dto.testTime,
      };
    },
    generatePreviewDataAndHeaders(preparedRows) {
      let previewData = [];
      const currentTime = moment();
      this.warningMessage = ""; // Reset warning message
      this.futureDateFound = false;

      preparedRows.forEach((row, index) => {
        let flattenedRow = { "Row Number": index + 1 };

        if (row.averoutN) {
          flattenedRow["Document No."] = row.averoutN;
        }

        for (const [key, value] of Object.entries(row)) {
          if (key !== "elements" && key !== "averoutN") {
            flattenedRow[key] = value;
          }
        }

        (row.elements || []).forEach((element) => {
          if (element && element.element != null) {
            flattenedRow[element.element] = element.compositionPercent;
          }
        });

        if (row.testTime && moment(row.testTime).isAfter(currentTime)) {
          this.warningMessage += `Row number ${
            index + 1
          } has a future test time. `;
          this.futureDateFound = true;
        }

        previewData.push(flattenedRow);
      });

      // Files may carry different element sets, so headers are the union of all
      // rows' keys rather than just the first row's.
      const previewHeaders = [];
      previewData.forEach((r) => {
        Object.keys(r).forEach((k) => {
          if (!previewHeaders.includes(k)) previewHeaders.push(k);
        });
      });

      return {
        previewData,
        previewHeaders,
      };
    },
    confirmUpload() {
      this.loading = true;

      // Upload the prepared rows in previewData
      this.uploadRows(this.preparedRows)
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.addErrorMessage("Error uploading file");
          this.loading = false;
        });
      this.previewDialog = false;
    },
    mapRows(row) {
      const date = row.Date ? row.Date : row["Analysis Date"];
      const time = row.Time;
      const currentYear = new Date().getFullYear();
      let dateTime = moment(`${date} ${time}`, "DD/MM/YYYY HH:mm:ss");

      const rowYear = dateTime.year();
      if (rowYear - currentYear > 500) {
        dateTime = dateTime.subtract(543, "years");
      }

      let fields = [
        "Pb",
        "Sn",
        "Sb",
        "Ca",
        "Al",
        "Bi",
        "Cu",
        "Ni",
        "As",
        "Ag",
        "Fe",
        "Zn",
        "Te",
        "Se",
        "S",
        "Cd",
      ];

      const elements = fields.map((f) => {
        const id = this.getElementId(f.toLowerCase());
        const element = f;
        const numb = row[f];
        let lessThan = false;
        if (numb.includes("<")) {
          lessThan = true;
        }
        const parsed = parseFloat(numb.replace("<", ""));
        return {
          labElementId: id,
          element,
          compositionPercent: parsed,
          lessThan,
        };
      });

      return {
        sampleName: row.SampleNo ? row.SampleNo : row["Sample No."],
        quality: row.Quality,
        averoutN: row.AverOutN ? row.AverOutN : row.Charge,
        program: row.Program,
        technician: row.QMDelete ? row.QMDelete : row.Operator,
        elements,
        testTime: dateTime.format("YYYY-MM-DDTHH:mm:ss"),
      };
    },
    getElementId(symbol) {
      const match = this.allElements.find((e) => e.symbol == symbol);
      return match ? match.id : null;
    },
    getElementSymbol(id) {
      const match = (this.allElements || []).find((e) => e.id === id);
      return match ? match.symbol : null;
    },
    async uploadRows(rows) {
      if (rows && rows.length > 0) {
        // Remove the 'element' property from each element in each row
        const modifiedRows = rows.map((row) => {
          const elements = row.elements.map((el) => ({
            labElementId: el.labElementId,
            compositionPercent: el.compositionPercent,
            lessThan: el.lessThan,
          }));
          return { ...row, elements };
        });

        this.createAllInventoryLabResults(modifiedRows)
          .then((response) => {
            let counter = 0;
            counter = response.length;
            let skippedCount = rows.length - counter;

            if (skippedCount > 0) {
              this.addMessage(
                `Uploaded ${counter} rows successfully with ${skippedCount} rows skipped.`
              );
            } else {
              this.addSuccessMessage("Uploaded " + counter + " rows");
            }
            this.goBack();
          })
          .catch((err) => {
            console.error(err);
            this.addErrorMessage("Error uploading lab results");
          });
      }
    },
    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>
<style scoped>
.nowrap {
  white-space: nowrap;
}
.table-scroll {
  overflow-x: auto;
}
</style>
